import { useState, useEffect } from "react";
import { Settings as SettingsIcon, User, Clock, Tags, Plus, Trash2, LayoutGrid, MapPin } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  getProfile, 
  getTimeSlots, 
  updateTimeSlot, 
  createSlot,
  getAccountCategories, 
  createAccountCategory, 
  deleteAccountCategory,
  getSystemSettings,
  updateSystemSettings,
  getDisposalSites,
  createDisposalSite,
  updateDisposalSiteStatus
} from "@/services/api";

export const Settings = () => {
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: ""
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newSlot, setNewSlot] = useState({ name: '', value: '', id: '' });
  const [systemSettings, setSystemSettings] = useState({
    enable_normal_pickup: true,
    enable_recurring_pickup: true
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [disposalSites, setDisposalSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [newSite, setNewSite] = useState({ disposal_name: "", location: "", status: "open", contact: "" });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile();
        if (data && data.success && data.profile) {
          setProfile(data.profile);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
    fetchSlots();
    fetchCategories();
    fetchSystemSettings();
    fetchDisposalSites();
  }, []);

  const fetchDisposalSites = async () => {
    setLoadingSites(true);
    try {
      const data = await getDisposalSites();
      if (data && data.success) {
        setDisposalSites(data.sites);
      }
    } catch (error) {
      console.error("Error fetching disposal sites:", error);
      toast.error("Failed to load disposal sites");
    } finally {
      setLoadingSites(false);
    }
  };

  const handleAddDisposalSite = async () => {
    if (!newSite.disposal_name || !newSite.location || !newSite.status || !newSite.contact) {
      toast.error("Please fill in all disposal site fields");
      return;
    }
    try {
      const data = await createDisposalSite(newSite);
      if (data && data.success) {
        toast.success("Disposal site added successfully");
        setNewSite({ disposal_name: "", location: "", status: "open", contact: "" });
        fetchDisposalSites();
      }
    } catch (error) {
      console.error("Error adding disposal site:", error);
      toast.error(error.response?.data?.message || "Failed to add disposal site");
    }
  };

  const handleToggleDisposalSiteStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'open' ? 'close' : 'open';
      const data = await updateDisposalSiteStatus(id, newStatus);
      if (data && data.success) {
        toast.success(`Disposal site status updated to ${newStatus}`);
        fetchDisposalSites();
      }
    } catch (error) {
      console.error("Error updating disposal site:", error);
      toast.error("Failed to update disposal site status");
    }
  };

  const fetchSlots = async () => {
    setLoadingSlots(true);
    try {
      const data = await getTimeSlots();
      if (data && data.success) {
        setSlots(data.slots);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Failed to load time slots");
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const data = await getAccountCategories();
      if (data && data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load account categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSystemSettings = async () => {
    setLoadingSettings(true);
    try {
      const data = await getSystemSettings();
      if (data && data.success) {
        setSystemSettings(data.settings);
      }
    } catch (error) {
      console.error("Error fetching system settings:", error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleToggleSetting = async (key, value) => {
    try {
      const updates = { [key]: value };
      const data = await updateSystemSettings(updates);
      if (data && data.success) {
        setSystemSettings({ ...systemSettings, [key]: value });
        toast.success("System settings updated");
      }
    } catch (error) {
      toast.error("Failed to update system settings");
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const data = await createAccountCategory({ name: newCategory.trim() });
      if (data && data.success) {
        toast.success("Category added successfully");
        setNewCategory("");
        fetchCategories();
      }
    } catch (error) {
      toast.error("Failed to add category");
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      const data = await deleteAccountCategory(id);
      if (data && data.success) {
        toast.success("Category deleted successfully");
        fetchCategories();
      }
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleToggleSlot = async (id, currentStatus) => {
    try {
      const data = await updateTimeSlot(id, { isActive: !currentStatus });
      if (data && data.success) {
        toast.success("Time slot updated successfully");
        fetchSlots();
      }
    } catch (error) {
      console.error("Error updating slot:", error);
      toast.error("Failed to update time slot");
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot.name || !newSlot.value || !newSlot.id) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      const data = await createSlot(newSlot);
      if (data && data.success) {
        toast.success("Time slot created successfully");
        setNewSlot({ name: '', value: '', id: '' });
        fetchSlots();
      }
    } catch (error) {
      console.error("Error creating slot:", error);
      toast.error("Failed to create time slot");
    }
  };

  const handleSaveProfile = () => {
    toast.success(`Profile settings saved successfully!`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your admin account and system preferences</p>
      </div>

      <Tabs defaultValue="slots" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-[1000px]">
          <TabsTrigger value="slots">
            <Clock className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Time Slots</span>
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Tags className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">User Types</span>
          </TabsTrigger>
          <TabsTrigger value="system">
            <LayoutGrid className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">System</span>
          </TabsTrigger>
          <TabsTrigger value="disposal-sites">
            <MapPin className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Disposal Sites</span>
          </TabsTrigger>
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
        </TabsList>

        {/* Time Slots Tab */}
        <TabsContent value="slots" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Pickup Time Slots</CardTitle>
              <CardDescription>Enable or disable pickup time slots available to users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Time Slot Form */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-4">Add New Time Slot</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="slot-name">Slot Name</Label>
                    <Input 
                      id="slot-name"
                      placeholder="e.g., Morning Slot" 
                      value={newSlot.name}
                      onChange={(e) => setNewSlot({...newSlot, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slot-value">Time Range</Label>
                    <Input 
                      id="slot-value"
                      placeholder="e.g., 9:00 AM - 12:00 PM" 
                      value={newSlot.value}
                      onChange={(e) => setNewSlot({...newSlot, value: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slot-id">Slot ID</Label>
                    <Input 
                      id="slot-id"
                      placeholder="e.g., morning" 
                      value={newSlot.id}
                      onChange={(e) => setNewSlot({...newSlot, id: e.target.value})}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAddSlot} disabled={!newSlot.name || !newSlot.value || !newSlot.id}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Time Slot
                  </Button>
                  <Button variant="outline" onClick={() => setNewSlot({name: '', value: '', id: ''})}>
                    Clear
                  </Button>
                </div>
              </div>

              {/* Existing Time Slots */}
              {loadingSlots ? (
                <div className="flex justify-center p-4">Loading slots...</div>
              ) : (
                slots.map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-medium text-foreground">{slot.name}</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Time Range: {slot.value}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm px-2 py-1 rounded ${slot.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {slot.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <Switch
                        checked={slot.isActive}
                        onCheckedChange={() => handleToggleSlot(slot.id, slot.isActive)}
                      />
                    </div>
                  </div>
                ))
              )}
              {slots.length === 0 && !loadingSlots && (
                <div className="text-center p-4 text-muted-foreground">
                  No time slots configured.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Pickup Flow Configuration</CardTitle>
              <CardDescription>Enable or disable different pickup scheduling methods for users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">Normal Pickup</h4>
                  <p className="text-sm text-muted-foreground mt-1">Allow users to schedule one-time pickups with specific date and time slots</p>
                </div>
                <Switch
                  checked={systemSettings.enable_normal_pickup}
                  onCheckedChange={(checked) => handleToggleSetting('enable_normal_pickup', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">Recurring Pickup</h4>
                  <p className="text-sm text-muted-foreground mt-1">Allow users to schedule recurring pickups (Everyday, Alternative days, Weekly, Monthly)</p>
                </div>
                <Switch
                  checked={systemSettings.enable_recurring_pickup}
                  onCheckedChange={(checked) => handleToggleSetting('enable_recurring_pickup', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Disposal Sites Tab */}
        <TabsContent value="disposal-sites" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Disposal Sites</CardTitle>
              <CardDescription>Manage disposal sites and their status (Open/Close)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Disposal Site Form */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-4">Add New Disposal Site</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="site-name">Disposal Name</Label>
                    <Input 
                      id="site-name"
                      placeholder="e.g., City Center Landfill" 
                      value={newSite.disposal_name}
                      onChange={(e) => setNewSite({...newSite, disposal_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="site-contact">Contact Number</Label>
                    <Input 
                      id="site-contact"
                      placeholder="e.g., +91 98765 43210" 
                      value={newSite.contact}
                      onChange={(e) => setNewSite({...newSite, contact: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="site-location">Location (Address, Maps Link, or Coordinates)</Label>
                    <Input 
                      id="site-location"
                      placeholder="e.g., https://maps.app.goo.gl/..." 
                      value={newSite.location}
                      onChange={(e) => setNewSite({...newSite, location: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <select 
                      className="w-full flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={newSite.status}
                      onChange={(e) => setNewSite({...newSite, status: e.target.value})}
                    >
                      <option value="open">Open</option>
                      <option value="close">Close</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleAddDisposalSite} disabled={!newSite.disposal_name || !newSite.location || !newSite.status || !newSite.contact}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Disposal Site
                  </Button>
                  <Button variant="outline" onClick={() => setNewSite({disposal_name: '', location: '', status: 'open', contact: ''})}>
                    Clear
                  </Button>
                </div>
              </div>

              {/* Existing Disposal Sites */}
              {loadingSites ? (
                <div className="flex justify-center p-4">Loading disposal sites...</div>
              ) : (
                <div className="space-y-3">
                  {disposalSites.map((site) => (
                    <div key={site.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <h4 className="font-medium text-foreground">{site.disposal_name}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {site.location?.formattedAddress || "No address provided"}
                        </p>
                        {site.contact && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Contact: {site.contact}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm px-2 py-1 rounded capitalize ${site.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {site.status}
                        </span>
                        <div className="flex items-center space-x-2">
                          <Label className="text-sm text-muted-foreground">Toggle Status:</Label>
                          <Switch
                            checked={site.status === 'open'}
                            onCheckedChange={() => handleToggleDisposalSiteStatus(site.id, site.status)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {disposalSites.length === 0 && !loadingSites && (
                <div className="text-center p-4 text-muted-foreground">
                  No disposal sites configured.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Account Categories</CardTitle>
              <CardDescription>Manage user types available in registration and pickup flows</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-2">
                <Input 
                  placeholder="New Category Name (e.g. Household)" 
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <Button onClick={handleAddCategory}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>

              <div className="space-y-2">
                {loadingCategories ? (
                  <div className="flex justify-center p-4">Loading categories...</div>
                ) : (
                  categories.map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Tags className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-medium text-foreground">{category.name}</h4>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteCategory(category.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
                {categories.length === 0 && !loadingCategories && (
                  <div className="text-center p-8 border rounded-lg border-dashed text-muted-foreground">
                    No categories configured. Add your first one above.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your admin account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name">First Name</Label>
                  <Input id="first-name" value={profile.firstName} onChange={(e) => setProfile({...profile, firstName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name">Last Name</Label>
                  <Input id="last-name" value={profile.lastName} onChange={(e) => setProfile({...profile, lastName: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={profile.email} readOnly />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} />
              </div>
              <Button
                onClick={handleSaveProfile}
                className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-primary-foreground"
              >
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
